import { z } from 'zod';
export const ChatMessageRoleSchema = z.enum(['system', 'user', 'assistant', 'tool']);
/**
 * ToolCallRecord 의 externalRef — 외부 시스템 로그/세션 참조.
 * 현재는 Insight Agent 만 지원 — analysis.verify_tag 가 채움.
 * 어드민이 같은 row_id 로 에이전트 측 로그를 조회할 수 있도록 키만 보관.
 */
export const ToolCallExternalRefSchema = z.object({
    kind: z.literal('insight_agent'),
    /** Insight Agent 의 session_id — 우리 chat session_id 와 일치하도록 주입됨. */
    sessionId: z.string(),
    /** Insight Agent 의 row_id — 한 분석 시퀀스 (verify_tag~get_data) 안에서 공유. */
    rowId: z.number().int().nonnegative(),
    /**
     * row_id 가 응답으로 받은 값이 아니라 DB 의 기존 externalRef 들로부터 추정된 경우 true.
     * verify_tag API 자체가 실패해 응답이 없을 때 fallback 으로 max + 1 추정.
     */
    inferred: z.boolean().optional(),
});
/**
 * assistant 메시지에 저장되는 툴 호출 기록.
 * `input` / `output` 은 툴별 스키마에 따라 임의 JSON 이 들어갈 수 있어 `unknown`.
 */
/**
 * 기록되는 toolKey 는 서버 SkillToolKey enum 뿐 아니라 데스크톱 클라이언트(inniework)가
 * `clientTools` 로 정의한 client tool 이름(예: fs_write, run_command, browser_navigate)도
 * 될 수 있다. enum 으로 고정하면 client tool 을 호출한 세션이 GET 시
 * ChatSessionDetailSchema.parse 에서 깨진다(클라이언트 키는 enum 밖). 따라서 런타임 검증은
 * dotted 서버 키 + snake_case 클라이언트 키를 모두 허용하도록 넓히되, 다운스트림 코드는
 * SkillToolKey 로 좁혀 다루므로(=캐스트 다수) 추론 타입은 그대로 유지한다.
 */
const RecordedToolKeySchema = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_.]*$/);
export const ToolCallRecordSchema = z.object({
    id: z.string(),
    toolKey: RecordedToolKeySchema,
    input: z.unknown(),
    status: z.enum(['ok', 'error']),
    summary: z.string(),
    output: z.unknown().nullable(),
    error: z.string().nullable(),
    latencyMs: z.number().int(),
    /**
     * 외부 시스템 (현재 Insight Agent) 의 로그/세션 참조. analysis.verify_tag 만 채움.
     * 어드민 채팅 로그가 이걸 보고 "에이전트 로그 보기" 버튼을 노출.
     */
    externalRef: ToolCallExternalRefSchema.nullable().optional(),
    /**
     * Gemini 2.5+/3.x thinking 모델이 함수 호출과 함께 반환하는 base64 thought_signature.
     * 다음 턴에 같은 functionCall part 로 그대로 echo 해 보내지 않으면 Vertex AI 가
     * 400 INVALID_ARGUMENT ("missing thought_signature") 로 거부. Anthropic/OpenAI 는
     * 이 개념이 없어 항상 undefined — 무해.
     */
    thoughtSignature: z.string().nullable().optional(),
});
export const ChatMessageSchema = z.object({
    id: z.string().uuid(),
    role: ChatMessageRoleSchema,
    content: z.string(),
    attachedFileIds: z.array(z.string().uuid()).nullable().optional(),
    /** assistant 메시지에만 존재. 툴 호출이 있었던 iteration 의 호출 기록. */
    toolCalls: z.array(ToolCallRecordSchema).nullable().optional(),
    /** role='tool' 메시지에만 존재. LLM 이 발급한 call id 참조. */
    toolCallId: z.string().nullable().optional(),
    /** 같은 turn 의 마지막 assistant 메시지에만 set. user 메시지 INSERT 부터 done emit 까지의 wall-clock ms. */
    responseTimeMs: z.number().int().nullable().optional(),
    /**
     * assistant 메시지의 LLM 콜 입력 토큰. 어드민 트레이스 API 응답에만 포함되며,
     * 사용자 채팅 화면 API 는 명시적으로 select 하지 않아 노출되지 않는다.
     * usage 이벤트로 받은 실측값 또는 char heuristic 추정값.
     */
    inputTokens: z.number().int().nullable().optional(),
    /** assistant 메시지의 LLM 콜 출력 토큰. inputTokens 와 동일 정책. */
    outputTokens: z.number().int().nullable().optional(),
    /**
     * inputTokens 중 prompt cache 에서 hit 된 부분 (부분집합). estimate 폴백 또는
     * 캐싱 미활성 환경에서는 null.
     */
    cachedInputTokens: z.number().int().nullable().optional(),
    /**
     * 이번 턴에 prompt cache 에 쓰기 위해 청구된 토큰 (Anthropic 만 양수, 나머지는 null).
     */
    cacheCreationTokens: z.number().int().nullable().optional(),
    /** 'measured' = LLM 응답의 usage, 'estimated' = char heuristic 추정. */
    tokenSource: z.enum(['measured', 'estimated']).nullable().optional(),
    /**
     * compaction 으로 LLM 컨텍스트에서 제외된 시점 (ISO 문자열). NULL = 활성. UI 는 archived 메시지를
     * 회색/축약으로 표시하되 화면에 보존한다.
     */
    archivedAt: z.string().nullable().optional(),
    /**
     * role='system' compaction summary row 의 메타데이터.
     * `{ kind: 'compaction', summarizedMessageIds: string[], summarizedCount: number, summaryChars: number }`.
     */
    metadata: z.record(z.unknown()).nullable().optional(),
    createdAt: z.string(),
});
export const ChatSessionSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    isFavorite: z.boolean(),
    llmEndpointId: z.string().uuid().nullable(),
    llmBondingId: z.string().uuid().nullable(),
    skillId: z.string().uuid().nullable(),
    extendedThinking: z.boolean(),
    /** 소속 프로젝트. NULL = 미분류 ("최근 채팅" 영역에 노출). */
    projectId: z.string().uuid().nullable(),
    updatedAt: z.string(),
});
export const RenameChatSessionRequestSchema = z.object({
    title: z.string().trim().min(1).max(200),
});
export const ToggleChatSessionFavoriteRequestSchema = z.object({
    isFavorite: z.boolean(),
});
export const ChatSessionListSchema = z.object({
    sessions: z.array(ChatSessionSchema),
});
export const ChatSessionDetailSchema = ChatSessionSchema.extend({
    messages: z.array(ChatMessageSchema),
});
export const CreateChatSessionResponseSchema = z.object({
    id: z.string().uuid(),
});
/** 세션 생성 body. 미제출 허용 (skill 없이 빈 세션). */
export const CreateChatSessionRequestSchema = z
    .object({
    skillId: z.string().uuid().nullable().optional(),
    /** 지정 시 세션을 단일 엔드포인트에 고정. bondingId 와 상호배타. */
    endpointId: z.string().uuid().optional(),
    /** 지정 시 세션을 본딩(가상 LLM)에 고정. */
    bondingId: z.string().uuid().optional(),
    /** Anthropic 외 provider 에선 무시됨. 본딩 세션은 강제로 false. */
    extendedThinking: z.boolean().optional(),
})
    .refine((v) => !(v.endpointId && v.bondingId), {
    message: 'endpointId 와 bondingId 는 동시에 지정할 수 없습니다.',
    path: ['bondingId'],
})
    .optional();
/**
 * LLM function-call tool 정의 (provider-agnostic 형식). name 은 OpenAI 함수명
 * 규칙 (snake_case). parameters 는 JSON Schema 객체.
 *
 * 데스크톱 클라이언트 (inniework) 가 `CreateChatMessageRequest.clientTools` 로 보내는
 * 도구가 이 형식. 서버는 그것 그대로 LLM 에 expose 후, LLM 이 호출 시 client_tool_call
 * SSE 이벤트로 클라이언트에 위임 → 클라이언트가 결과 POST 로 회신.
 */
export const ClientToolDefinitionSchema = z.object({
    name: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/),
    description: z.string().min(1).max(4096),
    parameters: z.record(z.unknown()),
});
export const CreateChatMessageRequestSchema = z
    .object({
    content: z.string().min(1).max(10_000),
    fileIds: z.array(z.string().uuid()).max(10).optional(),
    /** 지정 시 세션을 단일 엔드포인트에 고정. bondingId 와 상호배타. */
    endpointId: z.string().uuid().optional(),
    /** 지정 시 세션을 본딩(가상 LLM)에 고정. 메시지마다 멤버 랜덤 선택. */
    bondingId: z.string().uuid().optional(),
    /** 지정 시 세션에 반영. Anthropic 외 provider 에선 무시됨. 본딩 세션은 강제로 false. */
    extendedThinking: z.boolean().optional(),
    /**
     * 마지막 사용자 메시지 편집·재전송용. 지정 시 서버가 이 메시지부터 이후(구 응답/도구 포함)를
     * 먼저 삭제한 뒤 새 user 메시지를 INSERT 하고 스트림을 시작한다. 클라이언트가 화면에 보이는
     * 마지막 user 메시지 id 를 넘긴다.
     */
    replaceFromMessageId: z.string().uuid().optional(),
    /**
     * 데스크톱 클라이언트가 LLM 에 노출할 client-side tool 목록.
     * 서버는 LLM 에 그대로 expose → LLM 이 호출 시 client_tool_call SSE 이벤트 emit,
     * `pending` 상태로 클라이언트 결과 POST 대기.
     * 웹 클라이언트는 미설정 → 기존 흐름 그대로.
     */
    clientTools: z.array(ClientToolDefinitionSchema).max(64).optional(),
})
    .refine((v) => !(v.endpointId && v.bondingId), {
    message: 'endpointId 와 bondingId 는 동시에 지정할 수 없습니다.',
    path: ['bondingId'],
});
/** 클라이언트가 client_tool_call 실행 후 결과를 POST 로 회신할 때 body. */
export const ClientToolResultRequestSchema = z.object({
    callId: z.string().min(1),
    status: z.enum(['ok', 'error']),
    output: z.unknown().optional(),
    error: z.string().max(2000).optional(),
});
/** 세션 모델 전환용 (`POST /api/chat/sessions/:id/model`). */
export const SetChatSessionModelRequestSchema = z
    .object({
    endpointId: z.string().uuid().optional(),
    bondingId: z.string().uuid().optional(),
    extendedThinking: z.boolean().optional(),
})
    .refine((v) => !(v.endpointId && v.bondingId), {
    message: 'endpointId 와 bondingId 는 동시에 지정할 수 없습니다.',
    path: ['bondingId'],
})
    .refine((v) => v.endpointId !== undefined || v.bondingId !== undefined || v.extendedThinking !== undefined, {
    message: 'endpointId, bondingId, extendedThinking 중 하나는 필요합니다.',
});
/**
 * Human-in-the-loop 인터럽트.
 * 서버가 사용자에게 선택지를 제시하고 응답을 받아 진행을 재개하는 일반 메커니즘.
 * 첫 적용처: skill 분류가 모호할 때 사용자에게 작업 종류를 묻는다.
 */
export const InterruptKindSchema = z.enum(['skill_select', 'ask_user', 'batch_ask_user']);
export const InterruptChoiceSchema = z.object({
    id: z.string(),
    label: z.string(),
    hint: z.string().optional(),
    /**
     * 색상 swatch — UI 가 라벨 옆에 작은 컬러 사각형으로 렌더.
     * 보통 OKLch 문자열 (예: "oklch(58% 0.18 255)") 5개 정도.
     * 대시보드 디렉션 선택 같은 비주얼 옵션에 사용.
     */
    swatch: z.array(z.string().max(80)).max(8).optional(),
});
/**
 * batch_ask_user 인터럽트 안의 개별 질문.
 * single = 단일 선택지 라디오 / multi = 복수 체크 / free = 자유 입력 (textarea).
 */
export const BatchQuestionKindSchema = z.enum(['single', 'multi', 'free']);
export const BatchQuestionSchema = z.object({
    id: z.string().min(1).max(60),
    title: z.string().min(1).max(300),
    kind: BatchQuestionKindSchema,
    /** single/multi 일 때 최소 2개. free 면 비워둠. */
    choices: z.array(InterruptChoiceSchema).optional(),
    /** free 질문의 placeholder. */
    placeholder: z.string().max(200).optional(),
});
export const InterruptPromptSchema = z.object({
    kind: InterruptKindSchema,
    title: z.string(),
    prompt: z.string(),
    choices: z.array(InterruptChoiceSchema),
    allowFreeText: z.boolean(),
    /** allowFreeText 가 true 일 때 "기타" 버튼에 표시할 라벨. 미지정 시 "기타". */
    freeTextLabel: z.string().max(20).optional(),
    /** allowFreeText 가 true 일 때 input placeholder. */
    freeTextPlaceholder: z.string().max(80).optional(),
    allowSkip: z.boolean(),
    defaultIndex: z.number().int().min(0),
    /** kind='batch_ask_user' 일 때만 사용. 단일 인터럽트로 여러 질문을 한 번에 묻는다. */
    questions: z.array(BatchQuestionSchema).optional(),
});
/**
 * batch 응답 — 질문 id 별 답변 묶음.
 * value 는 single 이면 string (choiceId), multi 면 string[] (choiceIds), free 면 string (text).
 * 답변 안 한 질문은 키가 없거나 빈 값.
 */
export const BatchAnswerSchema = z.object({
    questionId: z.string(),
    kind: BatchQuestionKindSchema,
    choiceId: z.string().optional(),
    choiceIds: z.array(z.string()).optional(),
    text: z.string().max(2000).optional(),
});
export const InterruptResolveRequestSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('choice'), choiceId: z.string() }),
    z.object({ kind: z.literal('free'), text: z.string().min(1).max(2000) }),
    z.object({ kind: z.literal('skip') }),
    z.object({ kind: z.literal('batch'), answers: z.array(BatchAnswerSchema) }),
]);
export const ActiveInterruptSchema = z.object({
    interruptId: z.string().uuid(),
    prompt: InterruptPromptSchema,
});
//# sourceMappingURL=chat.js.map